// AC GUIDE 5: WHAT TEMPERATURE SHOULD I SET MY AC TO? (Oct 2026)
// /blog/ideal-ac-temperature
//
// Rebuilt 20 Oct 2026 to GUIDES_PLAYBOOK.md section 3. The answer: set 24°C;
// at night, 26°C; never 18°C, which does not cool the room faster and only
// costs more. Behind it, Saheb's point: an AC cools as expected only when it
// can reach the setting; on a top floor on the hottest days it can't, and a
// colder setting does not help.
//
// Same rules as the other guides. Rupees come from the room model
// (data/roomDay.js) for one AC, the 1.5 ton 3 Star from roomACs(): the caller
// passes sweep() results. A month is 30 days like the one worked, ₹8 a unit.
// No imports. **double stars** mean bold.

export const TEMP_SLUG = 'blog/ideal-ac-temperature';
export const TEMP_PUBLISHED = '2026-10-19';
export const TEMP_UPDATED = '2026-10-20';

export const TEMP_SEO = {
  title: 'Best Temperature for AC: Set 24°C, and 26°C at Night | Citi Cool',
  meta: 'What is the best temperature for an AC? Set 24°C, and 26°C at night. Never 18°C: it does not cool faster, it only costs more. See what each degree saves.',
  eyebrow: 'AC Guide · Using your AC',
  h1: 'What is the best temperature to set your AC?',
  crumb: 'Best AC temperature',
  readMins: 4,
};

export const RATE = 8;
export const DAYS = 30;
export const SET_MIN = 18;
export const SET_MAX = 28;
export const SETS = Array.from({ length: SET_MAX - SET_MIN + 1 }, (_, i) => SET_MIN + i);
export const TOOL_SETS = [20, 22, 24, 26, 28];

// The rooms: 12 × 12 ft Delhi bedrooms with a west window.
export const BASE_ROOM = { city: 'delhi', kind: 'bedroom', size: '12x12', sun: 'W', setC: 24, day: 'typical' };
export const EVENING_ROOM = { ...BASE_ROOM, floor: 'mid', schedule: 'evening' };       // the animation and the tool
export const NIGHT_ROOM = { ...BASE_ROOM, floor: 'mid', schedule: 'night' };
export const HARD_ROOM = { ...BASE_ROOM, floor: 'top', schedule: 'afternoon', day: 'hottest' };

// ─── sources ──────────────────────────────────────────────────────────────

export const TEMP_SOURCES = [
  { key: 'pib-faq', label: 'Frequently Asked Questions on BEE recommendations on temperature setting of air conditioners, 29 June 2018', by: 'PIB, Ministry of Power', href: 'https://www.pib.gov.in/newsite/PrintRelease.aspx?relid=180281&reg=48&lang=2' },
  { key: 'pib-launch', label: 'Power Minister launches campaign to promote energy efficiency in air-conditioning, 22 June 2018', by: 'PIB, Ministry of Power', href: 'https://www.pib.gov.in/newsite/PrintRelease.aspx?relid=180130&reg=48&lang=2' },
  { key: 'mop-24', label: 'BEE notifies new energy performance standards for air conditioners: 24°C default from 1 January 2020, 6 January 2020', by: 'PIB, Ministry of Power', href: 'https://www.pib.gov.in/PressReleasePage.aspx?PRID=1598508' },
  { key: 'daikin-care', label: 'Product Care: comfort temperature, how long a room takes to cool, closing windows and curtains, cleaning filters', by: 'Daikin India', href: 'https://daikinindia.com/product-care' },
  { key: 'daikin-sleep', label: 'Enjoying sound sleep even during nights of extreme heat', by: 'Daikin India', href: 'https://daikinindia.com/blog-details/enjoying-sound-sleep-even-during-nights-extreme-heat' },
  { key: 'calc', label: "Our room model: Daikin's method for how much heat gets into a room, hour by hour, on IMD weather for Delhi, as in our AC calculator", by: 'Citi Cool engineering', href: '/ac-calculator' },
  { key: 'derate', label: "Daikin's full-cooling outside temperature for each series, as used in our AC calculator", by: 'Daikin · Citi Cool engineering', href: '/ac-calculator' },
];

// ─── arithmetic ───────────────────────────────────────────────────────────

export const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;
export const inr10 = (n) => `₹${(Math.round(n / 10) * 10).toLocaleString('en-IN')}`;

// Every setting from SET_MIN to SET_MAX for one room. runRoom(room) returns
// the AC's roomComparison() row: { units, flatHours, warmest }.
export function sweep(runRoom, r) {
  return SETS.map((setC) => {
    const x = runRoom({ ...r, setC });
    return { setC, units: x.units, rupees: x.units * DAYS * RATE, short: x.flatHours, warmest: x.warmest };
  });
}

// Builds runRoom for one AC from roomComparison() (data/roomDay.js).
export function makeRunRoom(roomComparison, HL, stations, ac) {
  const cache = {};
  return (r) => {
    const k = JSON.stringify(r);
    if (!cache[k]) {
      const cmp = roomComparison(HL, stations[r.city], r, [ac]);
      const x = cmp.rows[0];
      cache[k] = { units: x.units, flatHours: x.flatHours, warmest: x.warmest };
    }
    return cache[k];
  };
}

const at = (pts, c) => pts.find((x) => x.setC === c);

// The tool's sentence: what `setC` costs a month against 24°C.
export function settingText(pts, setC) {
  const a = at(pts, 24); const b = at(pts, setC);
  if (setC === 24) return `At **24°C**, this bedroom's AC costs about **${inr10(a.rupees)} a month**.`;
  const d = b.rupees - a.rupees;
  return `At **${setC}°C**, you pay about **${inr10(Math.abs(d))} a month ${d > 0 ? 'more' : 'less'}** than at 24°C.`;
}

// ─── the page ─────────────────────────────────────────────────────────────

// evening, night, hard: sweep() for EVENING_ROOM, NIGHT_ROOM, HARD_ROOM; code: the AC's code.
export function tempContent(evening, night, hard, code) {
  const e18 = at(evening, 18); const e24 = at(evening, 24); const e26 = at(evening, 26);
  const n24 = at(night, 24); const n26 = at(night, 26);
  const reach = at(hard, SET_MIN).short ? Math.round(at(hard, SET_MIN).warmest) : null;

  return {
    answer: {
      text: '**Set your AC to 24°C. At night, set 26°C. Never set 18°C.** It does not cool the room faster. It only costs more.',
      src: ['mop-24', 'daikin-sleep', 'pib-faq'],
    },
    because: [
      { text: 'Every star-labelled AC starts at 24°C. The Ministry of Power calls 24 to 25°C ideal.', src: ['mop-24', 'pib-faq'] },
      { text: 'Each degree warmer saves about 6% of the AC\'s electricity.', src: ['pib-faq'] },
      ...(reach && reach > 24 ? [{ text: `On a top floor on the hottest day, the room stops near ${reach}°C. A colder setting won't help.`, src: ['calc', 'derate'] }] : []),
    ],
    watch: {
      title: 'Watch: an AC set to 18°C and one set to 24°C',
      captions: [
        { at: 0, text: 'Two same rooms, two same ACs. One is set to 18°C. One is set to 24°C.' },
        { at: 0.2, text: 'Both rooms cool at the same speed. 18°C is not faster.' },
        { at: 0.45, text: 'At 24°C, one AC slows down. The other keeps going at full power.' },
        { at: 0.8, text: `In a month, 18°C costs about ${inr10(e18.rupees - e24.rupees)} more. Each degree warmer saves about 6%.` },
      ],
      note: `A 1.5 ton Daikin ${code} in a Delhi bedroom, used 6 pm to 2 am. At ${inr(RATE)} a unit.`,
      noteSrc: ['calc', 'pib-faq'],
    },
    table: {
      title: 'What temperature should I set my AC to?',
      head: ['Your situation', 'Set'],
      rows: [
        ['Day at home', '24 to 25°C'],
        ['Sleeping', '26 to 28°C'],
        ['Just walked into a hot room', 'Still 24°C'],
        ['You want the lowest bill', '26°C'],
        ['Office or shop', '24°C'],
      ],
      note: 'The rule: **start at 24°C, and go warmer if you feel fine.**',
      src: ['pib-faq', 'daikin-sleep', 'pib-launch'],
    },
    sides: [
      {
        key: 'dos',
        title: 'AC temperature: do\'s and don\'ts',
        goodHead: 'Do',
        badHead: 'Don\'t',
        good: [
          { text: 'Start at 24°C. Go warmer if you feel fine.', src: ['pib-faq'] },
          { text: 'At night, set 26 to 28°C and leave the AC on.', src: ['daikin-sleep'] },
          { text: 'Close doors and windows. Draw the curtains.', src: ['daikin-care'] },
          { text: 'Give a hot room 1 to 2 hours to cool.', src: ['daikin-care'] },
        ],
        bad: [
          { text: 'Don\'t set 18°C to cool faster.', src: ['pib-faq'] },
          { text: 'Don\'t use a timer to switch off at night. The room soon heats up again.', src: ['daikin-sleep'] },
          { text: 'Don\'t expect 24°C on a top floor on the hottest days. [Check your AC size](/ac-calculator).', src: ['calc'] },
        ],
      },
    ],
    parts: [
      {
        id: 'faster',
        q: 'Does 18°C cool a room faster?',
        bullets: [
          { text: 'No. The AC cools at the same speed at any setting.', src: ['pib-faq'] },
          { text: 'At 18°C it just runs longer at full power.', src: ['pib-faq'] },
          { text: `In our test bedroom, that costs about ${inr10(e18.rupees - e24.rupees)} more a month.`, src: ['calc'] },
        ],
      },
      {
        id: 'degree',
        q: 'How much does each degree save?',
        bullets: [
          { text: 'About 6% of the AC\'s electricity, says the Ministry of Power.', src: ['pib-faq'] },
          { text: `In a bedroom used in the evening, 26°C saves about ${inr10(e24.rupees - e26.rupees)} a month over 24°C.`, src: ['calc'] },
          { text: 'The harder your AC works, the more each degree saves.', src: ['calc'] },
        ],
      },
      {
        id: 'night',
        q: 'What temperature should I set my AC at night?',
        bullets: [
          { text: 'Daikin says 26 to 28°C for sound sleep.', src: ['daikin-sleep'] },
          { text: 'Leave the AC on all night. With a timer, the room can climb back to nearly 30°C.', src: ['daikin-sleep'] },
          { text: `26°C instead of 24°C saves about ${inr10(n24.rupees - n26.rupees)} a month in a bedroom at night.`, src: ['calc'] },
        ],
      },
      {
        id: 'reach',
        q: 'Why does my AC not reach the temperature I set?',
        bullets: [
          { text: 'The room may take in more heat than the AC can take out.', src: ['calc'] },
          ...(reach && reach > 24 ? [{ text: `On the hottest afternoon, a top-floor room stops near ${reach}°C.`, src: ['calc', 'derate'] }] : []),
          { text: 'If it happens on normal days, clean the filters. Then check the [AC size](/blog/what-size-ac-do-i-need).', src: ['daikin-care', 'calc'] },
        ],
      },
    ],
    faq: [
      {
        q: 'What is the best temperature for an AC?',
        a: '24°C. Every star-labelled AC starts there since 2020. Go warmer if you feel fine.',
        src: ['mop-24', 'pib-faq'],
      },
      {
        q: 'Is 24°C or 26°C better for an AC?',
        a: '24°C is the comfort setting. 26°C costs less: about 12% less electricity.',
        src: ['pib-faq'],
      },
      {
        q: 'What temperature should I set my AC at night?',
        a: '26 to 28°C, says Daikin. Leave it on all night rather than on a timer.',
        src: ['daikin-sleep'],
      },
      {
        q: 'Does a lower temperature use more electricity?',
        a: 'Yes. Each degree colder uses about 6% more. See [how many units an AC uses](/blog/ac-power-consumption-units-per-hour).',
        src: ['pib-faq'],
      },
      {
        q: 'Is 24°C compulsory on ACs in India?',
        a: 'Since January 2020, every star-labelled AC must start at 24°C. You can still change it with the remote.',
        src: ['mop-24'],
      },
      {
        q: 'How long does an AC take to cool a room?',
        a: 'Daikin says 1 to 2 hours on average. Closing windows and curtains helps.',
        src: ['daikin-care'],
      },
    ],
    tool: {
      title: 'Check what your setting costs',
      examples: [20, 26, 28].map((c) => settingText(evening, c)),
    },
  };
}
